// E8RectangleCutParallelogramPerimeterOpenTask.js
// Zadanie otwarte E8 (2026 zad. 20) - prostokat rozciety na trapez i trojkat, obwód rownolegloboku.

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import SvgMathLabel from "../../../components/diagrams/SvgMathLabel";
import EquationInput from "../../../components/inputs/EquationInput";
import SqrtInput from "../../../components/inputs/SqrtInput";
import { scoreStaged } from "../../openTaskScoring";

function rawStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v.raw === "string") return v.raw;
  if (typeof v.value === "string") return v.value;
  return "";
}

function buildData({ shortSide, longSide }) {
  const hypSquared = 2 * shortSide * shortSide;
  const rootCoeff = shortSide;
  const perimeterConstant = 2 * longSide;
  return { shortSide, longSide, hypSquared, rootCoeff, perimeterConstant, maxPoints: 3 };
}

function createOriginalTask() {
  return { type: "E8RectangleCutParallelogramPerimeterOpenTask", data: buildData({ shortSide: 3, longSide: 9 }), source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 20 }, tags: ["e8", "planimetria", "pitagoras", "rownoleglobok", "obwód", "zadanie-otwarte"] };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const cases = [{ shortSide: 2, longSide: 8 }, { shortSide: 3, longSide: 10 }, { shortSide: 4, longSide: 12 }, { shortSide: 5, longSide: 15 }];
  const item = cases[Math.floor(Math.random() * cases.length)];
  return { type: "E8RectangleCutParallelogramPerimeterOpenTask", data: buildData(item), source: { variant: "random", basedOn: "E8_2026_MAJ_20" }, tags: ["e8", "planimetria", "pitagoras", "rownoleglobok", "obwód", "zadanie-otwarte"] };
}

function normalize(text) { return rawStr(text).toLowerCase().replace(/\s+/g, "").replace(/,/g, "."); }
function answerLatex(data) { return `${data.perimeterConstant}+${2 * data.rootCoeff}\\sqrt{2}`; }
function normalizeSqrtText(value) {
  return normalize(value)
    .replace(/\\sqrt\{?2\}?/g, "sqrt2")
    .replace(/\u221A\(?2\)?/g, "sqrt2")
    .replace(/\\sqrt\{?([0-9]+)\}?/g, "sqrt$1")
    .replace(/\u221A\(?([0-9]+)\)?/g, "sqrt$1")
    .replace(/\*/g, "");
}
function hasExactSlantedSide(text, data) {
  const value = normalizeSqrtText(text);
  return value.includes(`${data.rootCoeff}sqrt2`) || value.includes(`sqrt${data.hypSquared}`);
}
function countNeedle(text, needle) {
  return text.split(needle).length - 1;
}
function hasPythagorasStep(text, data) {
  const value = normalizeSqrtText(text).replace(/\u00b2/g, "^2");
  const sideSquared = `${data.shortSide}^2`;
  const hasTwoLegs = countNeedle(value, sideSquared) >= 2;
  const hasHypSquared = value.includes(String(data.hypSquared));
  const hasSquaredUnknown = /(?:c|x|d|a|b|kn|kb|bok)\^?2/.test(value);
  return hasTwoLegs || (hasSquaredUnknown && hasHypSquared);
}
function hasExactPerimeter(text, data) {
  const value = normalizeSqrtText(text);
  return (
    value.includes(String(data.perimeterConstant)) &&
    (value.includes(`${2 * data.rootCoeff}sqrt2`) || value.includes(`2sqrt${data.hypSquared}`))
  );
}
function hasPerimeterMethod(text, data) {
  const value = normalizeSqrtText(text);
  return (
    hasExactPerimeter(text, data) ||
    (value.includes(`2${data.longSide}`) && value.includes(`2${data.rootCoeff}sqrt2`)) ||
    (value.includes(`${data.longSide}2`) && value.includes(`${data.rootCoeff}sqrt22`))
  );
}
function hasApproxPerimeter(text, data) {
  const parsedFromObject = text && typeof text === "object" && Number.isFinite(text.parsed) ? text.parsed : null;
  const parsed = parsedFromObject ?? Number(rawStr(text).replace(",", "."));
  if (!Number.isFinite(parsed)) return false;
  const exact = data.perimeterConstant + 2 * data.rootCoeff * Math.sqrt(2);
  return Math.abs(parsed - exact) < 0.05;
}

function Diagram() {
  const fill = "rgba(255,159,10,0.14)";
  // viewBox -18 -14 246 138; render 260px aby dwa rysunki zmiescily sie obok siebie
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 24, margin: "14px 0", flexWrap: "wrap" }}>
      <svg width="260" height="146" viewBox="-18 -14 246 138" role="img" aria-label="Prostokąt ABCD">
        <rect x="36" y="30" width="144" height="48" fill={fill} stroke="currentColor" strokeWidth="2" />
        <text x="108" y="104" fill="currentColor" textAnchor="middle" fontSize="13">Rysunek 1.</text>
        <SvgMathLabel x={36} y={20} math="D" width={18} height={18} fontSize={14} align="right" />
        <SvgMathLabel x={180} y={20} math="C" width={18} height={18} fontSize={14} align="left" />
        <SvgMathLabel x={36} y={90} math="A" width={18} height={18} fontSize={14} align="right" />
        <SvgMathLabel x={180} y={90} math="B" width={18} height={18} fontSize={14} align="left" />
      </svg>
      <svg width="260" height="146" viewBox="-18 -14 246 138" role="img" aria-label="Równoległobok KLMN">
        <polygon points="42,78 156,78 126,30 12,30" fill={fill} stroke="currentColor" strokeWidth="2" />
        <text x="84" y="104" fill="currentColor" textAnchor="middle" fontSize="13">Rysunek 2.</text>
        <SvgMathLabel x={12} y={20} math="N" width={18} height={18} fontSize={14} align="right" />
        <SvgMathLabel x={126} y={20} math="M" width={18} height={18} fontSize={14} align="left" />
        <SvgMathLabel x={42} y={90} math="K" width={18} height={18} fontSize={14} align="right" />
        <SvgMathLabel x={156} y={90} math="L" width={18} height={18} fontSize={14} align="left" />
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return <div style={{ textAlign: "left", lineHeight: 1.7 }}><p>Z kartonu wycięto prostokąt <InlineMath math="ABCD" /> o wymiarach <InlineMath math={`${data.shortSide}`} /> i <InlineMath math={`${data.longSide}`} />. Następnie ten prostokąt rozcięto na dwie figury: trapez prostokątny oraz trójkąt prostokątny równoramienny. Z tych figur złożono równoległobok <InlineMath math="KLMN" />, który nie jest prostokątem.</p><Diagram /><p>Oblicz obwód równoległoboku <InlineMath math="KLMN" />. Zapisz obliczenia.</p></div>;
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  const set = (field) => (val) => onChange?.({ ...ua, [field]: val });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div style={{ fontSize: 12.5, opacity: 0.78, lineHeight: 1.45, borderLeft: "3px solid var(--color-accent)", paddingLeft: 8 }}>
        <strong>{"Wyniki po\u015brednie s\u0105 opcjonalne."}</strong>{" "}
        {"Wpisz je, je\u015bli chcesz dosta\u0107 punkty cz\u0105stkowe. Poprawny dok\u0142adny obw\u00f3d daje pe\u0142ne 3 punkty."}
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Twierdzenie Pitagorasa (opcjonalnie):</div>
        <EquationInput
          value={ua.pythagoras || ""}
          onChange={set("pythagoras")}
          disabled={disabled}
          placeholder={`np. c^2=${data.shortSide}^2+${data.shortSide}^2=${data.hypSquared}`}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>{"D\u0142ugo\u015b\u0107 boku sko\u015bnego:"}</div>
        <SqrtInput
          value={ua.slantedSide || ""}
          onChange={set("slantedSide")}
          disabled={disabled}
          label="c"
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>{"Spos\u00f3b obliczenia obwodu (opcjonalnie):"}</div>
        <EquationInput
          value={ua.perimeterMethod || ""}
          onChange={set("perimeterMethod")}
          disabled={disabled}
          placeholder={`np. Ob=2*${data.longSide}+2*${data.rootCoeff}sqrt2`}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>{"Obw\u00f3d:"}</div>
        <SqrtInput
          value={ua.perimeter || ""}
          onChange={set("perimeter")}
          disabled={disabled}
          label="Ob"
        />
      </div>
    </div>
  );
}
export function checkAnswer({ data }, userAnswer = {}) {
  const pyth = normalize(userAnswer.pythagoras);
  const slantedRaw = rawStr(userAnswer.slantedSide);
  const methodRaw = rawStr(userAnswer.perimeterMethod);
  const perimeterRaw = rawStr(userAnswer.perimeter);
  const pythagorasOk = hasPythagorasStep(pyth, data) || hasExactSlantedSide(slantedRaw, data);
  const methodOk = hasPerimeterMethod(methodRaw, data) || hasExactPerimeter(perimeterRaw, data);
  const perimeterMethodOk =
    methodOk ||
    (hasExactSlantedSide(slantedRaw, data) && (methodRaw + perimeterRaw).includes(String(data.perimeterConstant)));
  const resultOk = hasExactPerimeter(perimeterRaw, data);
  const approxOnlyOk = !resultOk && hasApproxPerimeter(userAnswer.perimeter, data);
  return scoreStaged({
    resultOk,
    milestones: [pythagorasOk || approxOnlyOk, perimeterMethodOk || approxOnlyOk],
    maxScore: data.maxPoints,
    parts: { "przeciwprostok\u0105tna": pythagorasOk || approxOnlyOk, "spos\u00f3b obliczenia obwodu": perimeterMethodOk || approxOnlyOk, "obw\u00f3d": resultOk },
  });
}
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`c^2=${data.shortSide}^2+${data.shortSide}^2=${data.hypSquared}`} />
      <BlockMath math={`c=${data.rootCoeff}\\sqrt{2}`} />
      <BlockMath math={`Ob=2\\cdot ${data.longSide}+2\\cdot ${data.rootCoeff}\\sqrt{2}=${answerLatex(data)}`} />
      <div>{"Odpowied\u017a: obw\u00f3d r\u00f3wnoleg\u0142oboku jest r\u00f3wny "}<InlineMath math={answerLatex(data)} />{"."}</div>
    </div>
  );
}

function rawLatexOrMissing(value) {
  const raw = rawStr(value);
  if (!raw) return "\\ \\text{brak}";
  return raw
    .replace(/\u221A\s*\(?([0-9]+)\)?/g, (_, n) => `\\sqrt{${n}}`)
    .replace(/sqrt\s*\(?([0-9]+)\)?/gi, (_, n) => `\\sqrt{${n}}`);
}

export function renderUserAnswer(userAnswer = {}) {
  const pythagoras = rawStr(userAnswer.pythagoras);
  const slantedSide = rawStr(userAnswer.slantedSide);
  const perimeterMethod = rawStr(userAnswer.perimeterMethod);
  const perimeter = rawStr(userAnswer.perimeter);
  if (!pythagoras && !slantedSide && !perimeterMethod && !perimeter) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>{"Twierdzenie Pitagorasa: "}<InlineMath math={rawLatexOrMissing(pythagoras)} /></div>
      <div>{"D\u0142ugo\u015b\u0107 boku sko\u015bnego: "}<InlineMath math={rawLatexOrMissing(slantedSide)} /></div>
      <div>{"Spos\u00f3b obliczenia obwodu: "}<InlineMath math={rawLatexOrMissing(perimeterMethod)} /></div>
      <div>{"Obw\u00f3d: "}<InlineMath math={rawLatexOrMissing(perimeter)} /></div>
    </div>
  );
}
export function renderExplanation({ data }) { return <div style={{ textAlign: "left", lineHeight: 1.75 }}><p><strong>Krok 1.</strong> Obliczamy przeciwprostokątną trójkąta prostokątnego równoramiennego.</p><BlockMath math={`c^2=${data.shortSide}^2+${data.shortSide}^2=${data.hypSquared}`} /><BlockMath math={`c=\\sqrt{${data.hypSquared}}=${data.rootCoeff}\\sqrt{2}`} /><p><strong>Krok 2.</strong> Równoległobok ma dwa boki długości <InlineMath math={`${data.longSide}`} /> i dwa boki długości <InlineMath math={`${data.rootCoeff}\\sqrt{2}`} />.</p><BlockMath math={`Ob=2\\cdot ${data.longSide}+2\\cdot ${data.rootCoeff}\\sqrt{2}=${answerLatex(data)}`} /><p>Obwód równoległoboku jest równy <InlineMath math={answerLatex(data)} />.</p></div>; }
